import { HttpErrorResponse } from '@angular/common/http';

/**
 * Thrown by handleResponse when the message comes from the server response, so it can be shown to the user as is.
 * The name stays 'Error' so that legacy code printing the error keeps its output.
 */
export class ServerMessageError extends Error { }

export abstract class BaseService {
  protected async handleResponse(thanable: Promise<any>): Promise<any> {
    const resp = await thanable;
    if (resp && resp.error) {
      if (typeof resp.error === 'string') {
        throw new ServerMessageError(resp.error);
      }

      if (typeof resp.error === 'object') {
        const serverMessage = resp.error.error || resp.error.message;
        if (serverMessage && typeof serverMessage === 'string') {
          throw new ServerMessageError(serverMessage);
        }

        throw new Error(serverMessage || JSON.stringify(resp.error));
      }

      throw new Error('Unknown error');
    }
    return resp;
  }

  // A blob request gets its error body as a Blob too. The API sends errors as JSON, so only a JSON body reaches
  // handleResponse; an empty body or a web server error page is not a message for the user.
  protected async readBlobError(request: Promise<unknown>): Promise<unknown> {
    const response = await request;
    if (!(response instanceof HttpErrorResponse) || !(response.error instanceof Blob)) {
      return response;
    }

    const body = await response.error.text();
    let error: unknown = null;
    try {
      error = JSON.parse(body);
    } catch {
      // Not JSON, so not an API error message.
    }

    if (!error) {
      throw new Error(body || 'Unknown error');
    }

    return { error };
  }

  protected validateBlob(blob: any) {
    if (!blob) {
      throw new Error('Stream is empty');
    }
    if (blob.type !== 'application/octet-stream') {
      throw new Error('Invalid stream');
    }
  }
}
